// src/App.js
import React, { useState } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import theme from './styles/theme';
import NavbarComponent from './components/Navbar/NavbarComponent';
import FooterComponent from './components/Footer/FooterComponent';
import SignupBanner from './components/Banner/SignupBanner';
import HomePage from './pages/Home';

const App = () => {
  // ✅ Safer iframe detection:
  // - window.top access can throw in cross-origin iframe cases.
  // - Using window.self !== window.top is the common pattern,
  //   but we still guard with try/catch to avoid crashing the app.
  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we can't access window.top due to cross-origin restrictions,
      // we assume we're in an iframe to prevent runtime crashes.
      return true;
    }
  };

  // ✅ Safe localStorage helpers:
  // In some environments (privacy mode, blocked storage, embedded contexts),
  // localStorage access can throw and prevent React from rendering.
  const safeLocalStorageGet = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };

  const safeLocalStorageSet = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      // Intentionally ignore. We still allow the UI to function
      // even if persistence isn't available.
    }
  };

  // Compute once per render to avoid repeated cross-origin checks
  const inIframe = isInIframe();

  // Persist dismissal to reduce annoyance/bounce
  const [bannerOpen, setBannerOpen] = useState(() => {
    // ✅ Use safe getter to avoid crashes in restricted contexts
    return safeLocalStorageGet('hideSignupBanner') !== '1';
  });

  const closeBanner = () => {
    // ✅ Use safe setter to avoid crashes in restricted contexts
    safeLocalStorageSet('hideSignupBanner', '1');
    setBannerOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {!inIframe && <NavbarComponent />}

        {/* ✅ Uses SignupBanner.jsx default copy (no message prop override) */}
        {!inIframe && (
          <Collapse in={bannerOpen} timeout={200}>
            <SignupBanner
              ctaLabel="Start free trial"
              ctaHref="https://app.utmsmartmanager.com/signup/"
              onClose={closeBanner}
            />
          </Collapse>
        )}

        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Define other routes here using the `element` prop */}
        </Routes>

        {!inIframe && <FooterComponent />}
      </Router>
    </ThemeProvider>
  );
};

export default App;